/**
 * Deliverables (roadmap step 7; decisions 839, 843; ADR-008; docs/specs/cloud/deliverables.md §3; help
 * `cloud.deliverables`). Staff choose a project and see its share links (add one, show it to the client, hide it,
 * withdraw it) and the processors' zips Teslyn Field uploaded after each daily report (R165, decision 925). A client
 * sees the links shown to their organisation and opens them; the address is asked for only when opened, so the
 * database checks, counts and audits each opening. Clients never see the processors' zips.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  canPortal,
  shareLinkProblems,
  shareLinkProvider,
  shareLinkState,
  shareLinkStateText,
  shortDate,
  sizeText,
  SHARE_LINK_PROVIDER_LABEL,
  SHARE_LINK_STATE_LABEL,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, TextArea, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadProjects, type PortalProject, type Scope } from '../portal/api.ts';
import { ProjectPicker } from '../portal/ProjectPicker.tsx';
import type { ReportsMode } from '../portal/ReportsList.tsx';
import {
  addShareLink,
  deliverableUrl,
  loadClientShareLinks,
  loadDeliverables,
  loadShareLinks,
  openShareLink,
  setShareLinkShown,
  withdrawShareLink,
  type ClientShareLink,
  type DeliverableRow,
  type ShareLinkRow,
} from './api.ts';
import { TableWrap } from '../components/TableWrap.tsx';
import { openInNewTab } from '../document-viewer/open.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const STATE_CHIP = {
  works: 'chip chip--pass',
  expired: 'chip chip--warn',
  withdrawn: 'chip chip--fail',
} as const;

export interface DeliverablesScreenProps {
  readonly sb: SupabaseClient;
  readonly scope: Scope;
  readonly mode: ReportsMode;
  readonly roles?: readonly string[];
  readonly now?: () => Date;
  /** Open an address in a new tab (tests record it). */
  readonly open?: (url: string) => void;
}

export function DeliverablesScreen({
  sb,
  scope,
  mode,
  roles = [],
  now = () => new Date(),
  open = openInNewTab,
}: DeliverablesScreenProps): ReactElement {
  const [projects, setProjects] = useState<PortalProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string>('');
  const reload = useCallback(() => {
    setError(null);
    loadProjects(sb, scope)
      .then((ps) => {
        setProjects(ps);
        setChosen((c) => c || ps[0]?.id || '');
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, scope]);
  useEffect(reload, [reload]);
  const project = projects?.find((p) => p.id === chosen) ?? null;

  return (
    <section className="page">
      <PageHeader
        title="Deliverables"
        subtitle={
          mode === 'staff'
            ? 'Share links to the data on your own storage, and the processors’ zips of each flight.'
            : 'Links to the survey data shared with you.'
        }
      />
      {error && <ErrorState message={error} onRetry={reload} />}
      {!projects && !error && <Loading what="projects" />}
      {projects && projects.length === 0 && <p className="body muted">No project to show.</p>}
      {projects && projects.length > 0 && (
        <ProjectPicker projects={projects} value={chosen} onChange={setChosen} />
      )}
      {project && mode === 'staff' && (
        <>
          <StaffLinks key={`l${project.id}`} sb={sb} project={project} roles={roles} now={now} open={open} />
          <ProcessorZips key={`z${project.id}`} sb={sb} project={project} open={open} />
        </>
      )}
      {project && mode !== 'staff' && (
        <ClientLinks key={project.id} sb={sb} project={project} now={now} open={open} />
      )}
    </section>
  );
}

function ClientLinks({
  sb,
  project,
  now,
  open,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly now: () => Date;
  readonly open: (url: string) => void;
}): ReactElement {
  const [links, setLinks] = useState<ClientShareLink[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const reload = useCallback(() => {
    setError(null);
    loadClientShareLinks(sb, project.id)
      .then(setLinks)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);
  async function go(l: ClientShareLink): Promise<void> {
    setNotice(null);
    try {
      open(await openShareLink(sb, l.id));
    } catch (e) {
      setNotice(message(e));
    }
  }
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!links) return <Loading what="share links" />;
  return (
    <div className="stack">
      {notice && (
        <p className="notice notice--fail" role="alert">
          {notice}
        </p>
      )}
      {links.length === 0 ? (
        <p className="body muted">No data link has been shared for {project.number} yet.</p>
      ) : (
        <ul className="stack" aria-label="Share links">
          {links.map((l) => (
            <li key={l.id} className="panel row row--wrap">
              <div className="grow">
                <p className="body">{l.label}</p>
                <p className="caption muted">
                  {SHARE_LINK_PROVIDER_LABEL[l.provider]}. {shareLinkStateText(l, now())}
                  {l.password_protected ? ' Password protected: the password is sent to you separately.' : ''}
                </p>
              </div>
              <Button variant="primary" onClick={() => void go(l)} aria-label={`Open ${l.label}`}>
                Open
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StaffLinks({
  sb,
  project,
  roles,
  now,
  open,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
  readonly now: () => Date;
  readonly open: (url: string) => void;
}): ReactElement {
  const [links, setLinks] = useState<ShareLinkRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const may = canPortal(roles, 'share_link.manage');
  const reload = useCallback(() => {
    setError(null);
    loadShareLinks(sb, project.id)
      .then(setLinks)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);

  async function run(key: string, what: () => Promise<void>, ok: string): Promise<void> {
    setBusy(key);
    setNotice(null);
    try {
      await what();
      setNotice({ ok: true, text: ok });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  }

  return (
    <section className="stack" aria-label="Share links">
      <h2 className="heading">Share links</h2>
      <p className="caption muted">
        The data stays on your own storage; Teslyn keeps the address and who may open it. Prefer a Nextcloud
        share with a password and an expiry date, and send the password to the client separately.
        {!project.clientOrgId &&
          ' No client organisation is linked to this project, so the client sees nothing yet.'}
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!links && !error && <Loading what="share links" />}
      {links && links.length === 0 && <p className="body muted">No share link yet.</p>}
      {links?.map((l) => {
        const state = shareLinkState(l, now());
        return (
          <div key={l.id} className="panel row row--wrap">
            <div className="grow">
              <p className="body">
                {l.label} <span className={STATE_CHIP[state]}>{SHARE_LINK_STATE_LABEL[state]}</span>
              </p>
              <p className="caption muted data">{l.url}</p>
              <p className="caption muted">
                {SHARE_LINK_PROVIDER_LABEL[l.provider]}. {shareLinkStateText(l, now())}
                {l.password_protected ? ' Password protected.' : ''}{' '}
                {l.shown && l.shown_at
                  ? `Shown to the client since ${shortDate(l.shown_at)}.`
                  : 'Not shown to the client.'}{' '}
                {l.last_opened_at
                  ? `Opened by the client ${l.opened_count} time${l.opened_count === 1 ? '' : 's'}, last ${shortDate(l.last_opened_at)}.`
                  : ''}
              </p>
              {l.note && <p className="caption">{l.note}</p>}
            </div>
            <Button variant="quiet" onClick={() => open(l.url)} aria-label={`Open ${l.label}`}>
              Open
            </Button>
            {may && state === 'works' && (
              <Button
                variant={l.shown ? 'quiet' : 'secondary'}
                busy={busy === `shown:${l.id}`}
                onClick={() =>
                  void run(
                    `shown:${l.id}`,
                    () => setShareLinkShown(sb, l.id, !l.shown),
                    l.shown
                      ? `${l.label} is no longer shown to the client.`
                      : `${l.label} is shown to the client.`,
                  )
                }
                aria-label={`${l.shown ? 'Hide from client' : 'Show to client'}: ${l.label}`}
              >
                {l.shown ? 'Hide from client' : 'Show to client'}
              </Button>
            )}
            {may && state !== 'withdrawn' && confirm !== l.id && (
              <Button variant="quiet" onClick={() => setConfirm(l.id)} aria-label={`Withdraw ${l.label}`}>
                Withdraw
              </Button>
            )}
            {confirm === l.id && (
              <>
                <span className="caption">The client can no longer open this link.</span>
                <Button
                  variant="primary"
                  busy={busy === `withdraw:${l.id}`}
                  onClick={() =>
                    void run(
                      `withdraw:${l.id}`,
                      () => withdrawShareLink(sb, l.id),
                      `${l.label} was withdrawn.`,
                    )
                  }
                >
                  Withdraw
                </Button>
                <Button variant="quiet" onClick={() => setConfirm(null)}>
                  Keep
                </Button>
              </>
            )}
          </div>
        );
      })}
      {may && project.status !== 'archived' && !adding && (
        <div className="row">
          <Button variant="secondary" onClick={() => setAdding(true)}>
            Add a share link
          </Button>
        </div>
      )}
      {adding && (
        <AddLink
          sb={sb}
          project={project}
          today={now().toISOString().slice(0, 10)}
          onDone={(text) => {
            setAdding(false);
            if (text) {
              setNotice({ ok: true, text });
              reload();
            }
          }}
        />
      )}
    </section>
  );
}

function AddLink({
  sb,
  project,
  today,
  onDone,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly today: string;
  readonly onDone: (text: string | null) => void;
}): ReactElement {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [password, setPassword] = useState(true);
  const [expires, setExpires] = useState('');
  const [note, setNote] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const provider = useMemo(() => shareLinkProvider(url), [url]);

  async function save(): Promise<void> {
    const p = shareLinkProblems({ label, url, expiresOn: expires, note }, today);
    setProblems(p);
    if (p.length > 0) return;
    setBusy(true);
    try {
      await addShareLink(sb, {
        projectId: project.id,
        label: label.trim(),
        url: url.trim(),
        provider,
        passwordProtected: password,
        expiresOn: expires || null,
        note: note.trim(),
      });
      onDone(`${label.trim()} was added. Press "Show to client" when the client should see it.`);
    } catch (e) {
      setProblems([message(e)]);
      setBusy(false);
    }
  }

  return (
    <div className="panel stack" role="group" aria-label="Add a share link">
      <TextField label="Label" value={label} onChange={setLabel} hint='Such as "Final data, block Milgun".' />
      <TextField
        label="Address"
        type="url"
        value={url}
        onChange={setUrl}
        hint={url.trim() ? `${SHARE_LINK_PROVIDER_LABEL[provider]} link.` : 'Paste the share link.'}
      />
      <CheckField
        label="The share has a password (sent to the client separately)"
        checked={password}
        onChange={setPassword}
      />
      <TextField label="Works until (optional)" type="date" value={expires} onChange={setExpires} />
      <TextArea label="Note for staff (optional)" value={note} onChange={setNote} />
      {problems.length > 0 && (
        <ul className="notice notice--fail" role="alert">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <div className="row">
        <Button variant="primary" busy={busy} onClick={() => void save()}>
          Add the link
        </Button>
        <Button variant="quiet" onClick={() => onDone(null)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function ProcessorZips({
  sb,
  project,
  open,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly open: (url: string) => void;
}): ReactElement {
  const [rows, setRows] = useState<DeliverableRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const reload = useCallback(() => {
    setError(null);
    loadDeliverables(sb, project.id)
      .then(setRows)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);
  async function download(d: DeliverableRow): Promise<void> {
    setNotice(null);
    try {
      open(await deliverableUrl(sb, d));
    } catch (e) {
      setNotice(message(e));
    }
  }
  return (
    <section className="stack" aria-label="For the processors">
      <h2 className="heading">For the processors</h2>
      <p className="caption muted">
        Teslyn Field zips each flight once its day’s daily report is published to the client, and emails the
        project’s data processors. Clients never see these.
      </p>
      {notice && (
        <p className="notice notice--fail" role="alert">
          {notice}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="processors’ zips" />}
      {rows && rows.length === 0 && (
        <p className="body muted">No zip yet: they come after the first daily report is published.</p>
      )}
      {rows && rows.length > 0 && (
        <TableWrap>
          <table className="table">
            <thead>
              <tr>
                <th className="label">Zip</th>
                <th className="label">Day and flight</th>
                <th className="label">Where</th>
                <th className="label">Sent</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td className="body">
                    <span className="data">{d.file_name}</span>
                    <div className="caption muted">
                      {sizeText(d.size_bytes)}, {d.contents.length} file{d.contents.length === 1 ? '' : 's'}
                    </div>
                  </td>
                  <td className="caption">
                    {shortDate(d.day)}, day {String(d.jday ?? '').padStart(3, '0')}, FLT
                    {String(d.flight_number ?? '').padStart(3, '0')}
                    <div className="muted">
                      Daily report revision {d.report_revision ?? '?'}
                      {d.replaced.length > 0
                        ? `; replaces the zip of revision ${d.replaced[d.replaced.length - 1]?.report_revision ?? '?'}`
                        : ''}
                    </div>
                  </td>
                  <td className="caption data">
                    {d.destination === 'nextcloud' ? `Nextcloud: ${d.path}` : 'Teslyn Cloud'}
                  </td>
                  <td className="caption">
                    {shortDate(d.uploaded_at)}
                    {d.uploaded_by_name ? ` by ${d.uploaded_by_name}` : ''}
                    <div className="muted">
                      {d.emailed > 0
                        ? `Emailed to the data processors (${d.emailed}).`
                        : 'No data processor emailed.'}
                    </div>
                  </td>
                  <td className="table__actions">
                    {d.destination === 'survera' && (
                      <Button
                        variant="quiet"
                        onClick={() => void download(d)}
                        aria-label={`Download ${d.file_name}`}
                      >
                        Download
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </section>
  );
}
